import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Globe, ChevronDown } from 'lucide-react'
import { applyDirection } from '@/i18n/config'
import { cn } from '@/utils/cn'
import {
  pathWithoutLang,
  localizePath,
  getLangFromPathname,
  SUPPORTED_LANG_CODES,
  type LangCode,
} from '@/utils/localeFromUrl'
import { languagesApi, type Language } from '@/services/languagesApiService'

function normalizeLangCode(code: string): string {
  return code.split('-')[0].toLowerCase()
}

/** Only languages the site has routes + i18n bundles for (admin must also enable them). */
function filterUiSupported(enabled: Language[]): Language[] {
  return enabled.filter((l) => SUPPORTED_LANG_CODES.includes(normalizeLangCode(l.code) as LangCode))
}

const API_ERROR_FALLBACK: Language[] = [
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl', isDefault: true },
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr', isDefault: false },
]

export default function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [languages, setLanguages] = useState<Language[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const enabled = await languagesApi.getEnabledLanguages()
        const list = filterUiSupported(enabled)
        if (cancelled) return
        setLanguages(list.length > 0 ? list : API_ERROR_FALLBACK)

        const cur = normalizeLangCode(i18n.language || 'ar')
        if (list.length > 0 && !list.some((l) => normalizeLangCode(l.code) === cur)) {
          const def = list.find((l) => l.isDefault) || list[0]
          const code = normalizeLangCode(def.code)
          await i18n.changeLanguage(code)
          applyDirection(code, def.direction)
        }
      } catch {
        if (!cancelled) setLanguages(API_ERROR_FALLBACK)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [i18n])

  const handleLanguageChange = async (langCode: string) => {
    const currentLang = getLangFromPathname(location.pathname)
    const restPath = pathWithoutLang(location.pathname, currentLang)
    const newPath = localizePath(langCode, restPath)

    try {
      localStorage.setItem('i18nextLng', langCode)
    } catch {
      /* ignore storage errors */
    }

    i18n.changeLanguage(langCode)
    const selectedLang = languages.find((l) => normalizeLangCode(l.code) === normalizeLangCode(langCode))
    if (selectedLang) {
      applyDirection(langCode, selectedLang.direction)
    } else {
      applyDirection(langCode)
    }
    languagesApi.clearCache()
    navigate({ pathname: newPath, search: location.search, hash: location.hash })
    setIsOpen(false)
  }

  const currentLanguage =
    languages.find((l) => normalizeLangCode(l.code) === normalizeLangCode(i18n.language || 'ar')) ||
    languages[0]

  return (
    <div className="relative" data-tour="language-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 transition-colors"
        aria-label="Change language"
        aria-expanded={isOpen}
      >
        {!loading && (
          <span className="text-sm hidden sm:inline">
            {currentLanguage?.nativeName || currentLanguage?.name || 'العربية'}
          </span>
        )}
        <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
        <Globe className="w-5 h-5 inline sm:hidden" />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute top-full mt-2 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 min-w-[150px]">
            {loading ? (
              <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
            ) : languages.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500">No languages available</div>
            ) : (
              languages.map((lang) => {
                const isCurrent = normalizeLangCode(i18n.language || '') === normalizeLangCode(lang.code)
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`block w-full px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                      isCurrent ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'
                    } ${lang.direction === 'rtl' ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{lang.nativeName}</span>
                      {lang.isDefault && (
                        <span className="text-xs text-gray-500 ml-2">(Default)</span>
                      )}
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </>
      )}
    </div>
  )
}

